import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import useSWR from 'swr';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { SortableTable } from '@/components/ui/sortable-table';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';

import formatNumber from '@/utils/formatNumber';

type ParticipantData = {
  id: string;
  firstName: string;
  lastName: string;
  group: {
    id: string;
    name: string;
  };
  number: number;
  totalDistance: number;
  sponsors: {
    id: string;
    firstName: string;
    lastName: string;
    contactMethod: string;
    address: string;
    amount: number;
    total: number;
    participant: string;
    verified: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  }[];
  createdAt: string;
  updatedAt: string;
  __v: number;
};

type FlatParticipant = {
  number: number;
  groupName: string;
  name: string;
  sponsorNames: string;
  hasSponsors: boolean;
  distance: number;
  earnings: number;
  actions: string;
  _original: ParticipantData;
};

type GroupOption = {
  id: string;
  name: string;
};

type ParticipantsTableProps = {
  isAdmin?: boolean;
};

const ParticipantsTable = ({ isAdmin = false }: ParticipantsTableProps) => {
  const { data, mutate } = useSWR<{ participants: ParticipantData[] }>(
    '/api/participants',
  );
  const { data: groupsData } = useSWR<{ groups: GroupOption[] }>(
    isAdmin ? '/api/groups' : null,
  );
  const groups = groupsData?.groups ?? [];

  const [isOpen, setIsOpen] = useState(false);
  const [editingParticipant, setEditingParticipant] =
    useState<ParticipantData | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
  }>({});

  const handleEdit = (participant: ParticipantData) => {
    setEditingParticipant(participant);
    setFirstName(participant.firstName);
    setLastName(participant.lastName);
    setGroupId(participant.group.id);
    setErrors({});
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setEditingParticipant(null);
    setFirstName('');
    setLastName('');
    setGroupId('');
    setErrors({});
  };

  const handleSubmit = async () => {
    const newErrors: { firstName?: string; lastName?: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'Feld ist erforderlich';
    }
    if (!lastName.trim()) {
      newErrors.lastName = 'Feld ist erforderlich';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!editingParticipant) return;

    const groupChanged =
      isAdmin && groupId && groupId !== editingParticipant.group.id;

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/updateParticipant', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: editingParticipant.id,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          ...(groupChanged ? { groupId } : {}),
        }),
      });

      if (response.ok) {
        mutate();
        toast({
          title: 'Teilnehmer erfolgreich aktualisiert!',
          status: 'success',
        });
        handleClose();
      } else if (response.status === 409) {
        toast({
          title: 'Teilnehmer konnte nicht verschoben werden',
          description:
            'Ein Teilnehmer mit diesem Namen existiert bereits im Zielstamm.',
          status: 'error',
        });
      } else {
        toast({
          title: 'Fehler beim Aktualisieren des Teilnehmers!',
          status: 'error',
        });
      }
    } catch (err) {
      toast({
        title: 'Fehler beim Aktualisieren des Teilnehmers!',
        status: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (participant: ParticipantData) => {
    if (
      !confirm(
        `Möchten Sie den Teilnehmer "${participant.firstName} ${participant.lastName}" wirklich löschen?`,
      )
    ) {
      return;
    }

    try {
      const response = await fetch('/api/updateParticipant', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: participant.id }),
      });

      if (response.ok) {
        mutate();
        toast({
          title: 'Teilnehmer gelöscht',
          status: 'success',
        });
      } else {
        const error = await response.json();
        toast({
          title: 'Fehler beim Löschen des Teilnehmers!',
          description: error.message,
          status: 'error',
        });
      }
    } catch (err) {
      toast({
        title: 'Fehler beim Löschen des Teilnehmers!',
        status: 'error',
      });
    }
  };

  if (!data || !data.participants) {
    return (
      <div className="flex justify-center">
        <Spinner size="xl" className="mt-16" />
      </div>
    );
  }

  const flatData: FlatParticipant[] = data.participants.map((p) => ({
    number: p.number,
    groupName: p.group.name,
    name: `${p.firstName} ${p.lastName}`,
    sponsorNames: p.sponsors
      .map((s) => `${s.firstName} ${s.lastName}`)
      .join('\n'),
    hasSponsors: p.sponsors.length > 0,
    distance: Number(p.totalDistance),
    earnings: p.sponsors.reduce((prev, curr) => prev + Number(curr.total), 0),
    actions: '',
    _original: p,
  }));

  return (
    <>
      <SortableTable
        data={flatData}
        keyColumn="number"
        defaultSort="number"
        defaultSortDirection="asc"
        columns={[
          { key: 'number', label: 'Startnummer' },
          { key: 'groupName', label: 'Stamm' },
          { key: 'name', label: 'Name' },
          {
            key: 'sponsorNames',
            label: 'Sponsoren',
            render: (v, row) =>
              row.hasSponsors ? (
                <span className="whitespace-pre-line">{v}</span>
              ) : (
                <span className="text-muted-foreground italic">
                  keine Sponsoren
                </span>
              ),
          },
          {
            key: 'distance',
            label: 'Gelaufene Strecke',
            render: (v) => `${formatNumber(v, 3)} km`,
          },
          {
            key: 'earnings',
            label: 'Einnahmen',
            render: (v) => `${formatNumber(v)} €`,
          },
          {
            key: 'actions',
            label: 'Aktionen',
            render: (_value, row) => (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEdit(row._original)}
                  aria-label="Teilnehmer bearbeiten"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {!row.hasSponsors && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleDelete(row._original)}
                    aria-label="Teilnehmer löschen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Teilnehmer bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-participant-firstname">
                Vorname <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-participant-firstname"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Vorname"
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-participant-lastname">
                Nachname <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-participant-lastname"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Nachname"
              />
              {errors.lastName && (
                <p className="text-sm text-red-500">{errors.lastName}</p>
              )}
            </div>

            {isAdmin && groups.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="edit-participant-group">Stamm</Label>
                <Select
                  id="edit-participant-group"
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                >
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Select>
                {editingParticipant &&
                  groupId !== editingParticipant.group.id && (
                    <p className="text-muted-foreground text-sm">
                      Der Wechsel ist nur möglich, wenn im Zielstamm noch kein
                      Teilnehmer mit diesem Namen existiert.
                    </p>
                  )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" className="mr-3" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Wird gespeichert...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ParticipantsTable;
